import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  UploadCloud,
  FileSpreadsheet,
  DownloadCloud,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Building2,
  Users,
  Info,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { importCustomers } from "../../../api/crm/customers/customerInfo";
import { downloadCustomerImportTemplate } from "./customerImportTemplateXlsx";

const COLUMN_GROUPS = [
  {
    title: "Thông tin khách hàng",
    color: "text-red-700",
    items: [
      { name: "Tên khách hàng *", desc: "Bắt buộc" },
      { name: "Số điện thoại", desc: "Tuỳ chọn" },
      { name: "Địa chỉ / Phường-Xã", desc: "Tuỳ chọn" },
      { name: "Nguồn khách", desc: "Mặc định: Referral" },
      { name: "Loại / Cấp học / Số HS", desc: "Cấp học bắt buộc nếu là Trường học" },
      { name: "Trạng thái / Ghi chú", desc: "Mặc định trạng thái: Mới" },
    ],
  },
  {
    title: "Người liên hệ 1 (khuyến nghị)",
    color: "text-sky-700",
    items: [
      { name: "Họ tên người liên hệ", desc: "Có tên → hệ thống tạo liên hệ" },
      { name: "Chức vụ người liên hệ", desc: "Tuỳ chọn" },
      { name: "SĐT người liên hệ", desc: "Tuỳ chọn" },
    ],
  },
  {
    title: "Người liên hệ 2 (tuỳ chọn)",
    color: "text-teal-700",
    items: [
      { name: "Họ tên / Chức vụ / SĐT người liên hệ 2", desc: "Thêm liên hệ thứ 2 nếu cần" },
    ],
  },
];

const StepLabel = ({ num, title, active, done }) => (
  <div
    className={`flex min-w-0 items-center gap-2 ${
      active ? "opacity-100" : done ? "opacity-70" : "opacity-40"
    }`}
  >
    <span
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
        active
          ? "bg-red-600 text-white"
          : done
            ? "bg-emerald-100 text-emerald-700"
            : "bg-gray-200 text-gray-500"
      }`}
    >
      {done && !active ? <CheckCircle2 size={14} /> : num}
    </span>
    <span className="truncate text-xs font-bold uppercase tracking-wide text-gray-700">
      {title}
    </span>
  </div>
);

const CustomerImportModal = ({
  isOpen,
  onClose,
  onRefresh,
  branchOptions = [],
  defaultBranchId = "",
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [branchId, setBranchId] = useState("");

  const resolvedDefaultBranchId = useMemo(() => {
    if (
      defaultBranchId &&
      branchOptions.some((opt) => String(opt.value) === String(defaultBranchId))
    ) {
      return defaultBranchId;
    }
    return branchOptions[0]?.value || "";
  }, [defaultBranchId, branchOptions]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setStep(1);
    setFile(null);
    setLoading(false);
    setTemplateLoading(false);
    setBranchId(resolvedDefaultBranchId);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, resolvedDefaultBranchId]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const name = selectedFile.name.toLowerCase();
    const ok =
      name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
    if (!ok) {
      showErrorToast("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
      return;
    }
    setFile(selectedFile);
  };

  const handleDownloadTemplate = async () => {
    try {
      setTemplateLoading(true);
      await downloadCustomerImportTemplate();
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tạo file mẫu Excel");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!branchId) return showErrorToast("Vui lòng chọn chi nhánh import");
    if (!file) return showErrorToast("Vui lòng chọn file danh sách");

    setLoading(true);
    try {
      const res = await importCustomers(file, branchId);
      showSuccessToast(
        res.message || `Import thành công ${res.imported} khách hàng`
      );
      if (res.errors?.length > 0 && res.errors.length <= 5) {
        res.errors.forEach((e) =>
          showErrorToast(`Dòng ${e.line}: ${e.message}`)
        );
      } else if (res.errors?.length > 5) {
        showErrorToast(
          `${res.errors.length} dòng lỗi. Hệ thống chỉ hiển thị 5 lỗi đầu.`
        );
      }
      onRefresh?.();
      onClose();
    } catch (err) {
      console.error(err);
      showErrorToast(
        err.response?.data?.message || "Lỗi khi import khách hàng"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <div className="pointer-events-none fixed inset-0 z-[210] flex items-end justify-center p-0 sm:items-center sm:p-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-3xl"
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6 sm:py-5">
                <div className="min-w-0">
                  <h2 className="flex items-center gap-2 text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                    <UploadCloud className="h-5 w-5 shrink-0 text-red-600 sm:h-6 sm:w-6" />
                    Nhập Excel khách hàng
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Chọn chi nhánh, tải mẫu Excel có định dạng, rồi import kèm
                    người liên hệ.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Steps */}
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-50 px-5 py-3 sm:px-6">
                <StepLabel num={1} title="Chi nhánh" active={step === 1} done={step > 1} />
                <ChevronRight size={14} className="shrink-0 text-gray-300" />
                <StepLabel num={2} title="File Excel" active={step === 2} done={step > 2} />
                <ChevronRight size={14} className="shrink-0 text-gray-300" />
                <StepLabel num={3} title="Import" active={step === 3} done={false} />
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                {step === 1 ? (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                        <Building2 size={14} /> Chi nhánh import
                      </label>
                      <Select
                        styles={customSelectStyles}
                        options={branchOptions}
                        placeholder="Chọn chi nhánh..."
                        value={
                          branchOptions.find(
                            (o) => String(o.value) === String(branchId)
                          ) || null
                        }
                        onChange={(opt) => setBranchId(opt?.value || "")}
                        isClearable={false}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        menuPosition="fixed"
                      />
                    </div>

                    <div className="flex gap-3 rounded-2xl border border-sky-100 bg-sky-50 p-4">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                      <p className="text-sm leading-relaxed text-sky-800">
                        Chi nhánh sẽ được gán cho toàn bộ khách hàng trong file.
                        Hệ thống mặc định ưu tiên chi nhánh của tài khoản đang
                        đăng nhập.
                      </p>
                    </div>
                  </div>
                ) : null}

                {step === 2 ? (
                  <div className="space-y-5">
                    <div className="relative rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center transition hover:border-red-300 hover:bg-red-50/20">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="absolute inset-0 z-10 cursor-pointer opacity-0"
                        onChange={handleFileChange}
                      />
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                        {file ? <CheckCircle2 size={28} /> : <FileSpreadsheet size={28} />}
                      </div>
                      <p className="text-sm font-bold text-gray-800">
                        {file
                          ? file.name
                          : "Kéo thả file vào đây hoặc bấm để chọn"}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Khuyến nghị .xlsx (mẫu hệ thống). Vẫn hỗ trợ .xls / .csv.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      disabled={templateLoading}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 py-3 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
                    >
                      {templateLoading ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <DownloadCloud size={16} />
                      )}
                      Tải file mẫu Excel (.xlsx)
                    </button>

                    <div className="grid gap-3 md:grid-cols-3">
                      {COLUMN_GROUPS.map((group) => (
                        <div
                          key={group.title}
                          className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4"
                        >
                          <p
                            className={`mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${group.color}`}
                          >
                            <Users size={13} />
                            {group.title}
                          </p>
                          <ul className="space-y-2">
                            {group.items.map((item) => (
                              <li key={item.name} className="text-sm text-gray-700">
                                <span className="font-semibold">{item.name}</span>
                                <span className="mt-0.5 block text-xs text-gray-500">
                                  {item.desc}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <p className="text-sm leading-relaxed text-amber-900">
                        File mẫu có sheet <strong>Hướng dẫn</strong> và sheet{" "}
                        <strong>Danh sách</strong> (định dạng cột, màu nhóm, dropdown
                        nguồn/loại/cấp học/trạng thái). Nhập dữ liệu vào sheet Danh
                        sách — mỗi dòng một khách hàng, khuyến nghị kèm ít nhất 1
                        người liên hệ.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
                  className="rounded-xl px-4 py-2.5 text-sm font-bold text-gray-500 transition hover:bg-gray-100"
                >
                  {step === 1 ? "Huỷ bỏ" : "Quay lại"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1) {
                      if (!branchId) {
                        showErrorToast("Vui lòng chọn chi nhánh import");
                        return;
                      }
                      setStep(2);
                      return;
                    }
                    handleUpload();
                  }}
                  disabled={loading || (step === 2 && !file)}
                  className="flex items-center gap-2 rounded-xl bg-red-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-100 transition hover:bg-red-700 active:scale-[0.98] disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : null}
                  {step === 1 ? "Tiếp theo" : "Bắt đầu import"}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

export default CustomerImportModal;
